// src/AccountPage.jsx
import * as React from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { SelectionContext } from "./selectionContext";
import { useAuth } from "./authContext";
import BrandLogo from "./components/branding/BrandLogo";
import "./styles/xnamai-account.css";
import "./styles/xnamai-account-modal.css";
import {
  AppBar, Box, Button, Chip, Container, CssBaseline, IconButton, Menu, MenuItem,
  Divider, Paper, Stack, ThemeProvider, Toolbar, Typography, createTheme,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, LinearProgress,
  TextField, Alert, Dialog, DialogContent
} from "@mui/material";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import AccountCircleRoundedIcon from "@mui/icons-material/AccountCircleRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { apiJoin, authHeaders, getJSON, postJSON } from "./lib/api";
import { isPaidStatus } from "./lib/paymentStatus";

// ▼ PIX
import PixModal from "./PixModal";
import { checkPixStatus, generateMainReservationPix } from "./services/pix";
// ▲ PIX
import AutoPaySection from "./AutoPaySection";
import {
  generatePromocionalPix,
  getMyPromocionalReservations,
} from "./modules/promocional/services/promocionalApi";

const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#256DFF" },
    secondary: { main: "#5EA8FF" },
    error: { main: "#E43D3D" },
    background: { default: "#F6FAFF", paper: "#FFFFFF" },
    success: { main: "#2F7FFF" },
    warning: { main: "#F4B740" },
  },
  shape: { borderRadius: 18 },
  typography: { fontFamily: ["Inter","system-ui","Segoe UI","Roboto","Arial"].join(",") },
});

const pad2 = (n) => String(n).padStart(2, "0");

function formatDrawLabel(row) {
  const drawNumber =
    row?.draw_number ?? row?.drawNumber ?? row?.draw_id ?? row?.drawId ?? null;
  const drawTitle =
    row?.draw_title ??
    row?.drawTitle ??
    row?.sorteio ??
    row?.title ??
    "Sorteio Principal";
  if (drawNumber != null && drawNumber !== "") {
    return `#${drawNumber} - ${drawTitle}`;
  }
  return String(drawTitle);
}

const XNAMAI_SITE_URL = "https://www.xnamai.com/";
const ADMIN_EMAIL = "admin@newstore.com.br";
const TTL_MINUTES = Number(process.env.REACT_APP_RESERVATION_TTL_MINUTES || 15);
const COUPON_VALIDITY_DAYS = Number(process.env.REACT_APP_COUPON_VALIDITY_DAYS || 180);

function formatDateTimeBR(value) {
  if (!value) return "--/--/---- --:--";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "--/--/---- --:--";
  }

  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatMoneyBRFromCents(cents) {
  return ((Number(cents) || 0) / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function normalizePurchaseHistoryPayload(payload) {
  const items = Array.isArray(payload?.items) ? payload.items : [];
  const lastPurchase = payload?.last_purchase || items[0] || null;

  return {
    items,
    lastPurchase,
  };
}

// chips
const PayChip = ({ status }) => {
  const st = String(status || "").toLowerCase();
  if (["expired", "expirado", "expirada"].includes(st)) {
    return (
      <Chip
        label="EXPIRADO"
        sx={{
          bgcolor: "rgba(15,23,42,0.08)",
          color: "#334155",
          fontWeight: 900,
          borderRadius: 999,
          px: 1.5,
          border: "1px solid rgba(15,23,42,0.14)",
        }}
      />
    );
  }
  if (isPaidStatus(st)) {
    return (
      <Chip
        label="PAGO"
        sx={{
          bgcolor: "rgba(37,109,255,0.12)",
          color: "#16325c",
          fontWeight: 900,
          borderRadius: 999,
          px: 1.5,
          border: "1px solid rgba(37,109,255,0.26)",
        }}
      />
    );
  }
  return (
    <Chip
      label="PENDENTE"
      sx={{
        bgcolor: "rgba(244,183,64,0.18)",
        color: "#6a4b00",
        fontWeight: 900,
        borderRadius: 999,
        px: 1.5,
        border: "1px solid rgba(244,183,64,0.35)",
      }}
    />
  );
};

const ResultChip = ({ result }) => {
  const r = String(result || "").toLowerCase();
  if (r.includes("contempla") || r.includes("win")) {
    return (
      <Chip
        label="CONTEMPLADO"
        sx={{
          bgcolor: "rgba(37,109,255,0.14)",
          color: "#16325c",
          fontWeight: 900,
          borderRadius: 999,
          px: 1.5,
          border: "1px solid rgba(37,109,255,0.30)",
        }}
      />
    );
  }
  if (r.includes("não") || r.includes("nao") || r.includes("n_contempla")) {
    return (
      <Chip
        label="NÃO CONTEMPLADO"
        sx={{
          bgcolor: "rgba(228,61,61,0.10)",
          color: "#9a1b1b",
          fontWeight: 900,
          borderRadius: 999,
          px: 1.5,
          border: "1px solid rgba(228,61,61,0.25)",
        }}
      />
    );
  }
  if (/(sorteado|closed|fechado)/.test(r)) {
    return (
      <Chip
        label={r.includes("sorteado") ? "SORTEADO" : "FECHADO"}
        sx={{
          bgcolor: "rgba(94,168,255,0.16)",
          color: "#16325c",
          fontWeight: 900,
          borderRadius: 999,
          px: 1.5,
          border: "1px solid rgba(94,168,255,0.32)",
        }}
      />
    );
  }
  return (
    <Chip
      label="ABERTO"
      sx={{
        bgcolor: "rgba(37,109,255,0.10)",
        color: "#16325c",
        fontWeight: 900,
        borderRadius: 999,
        px: 1.5,
        border: "1px solid rgba(37,109,255,0.22)",
      }}
    />
  );
};

const PlainStatusChip = ({ status }) => {
  const statusLabelMap = {
    active: "RESERVADO",
    reserved: "RESERVADO",
    pending: "RESERVADO",
    sold: "PAGO",
    paid: "PAGO",
    approved: "PAGO",
    blocked: "BLOQUEADO",
    closed: "ENCERRADO",
    inactive: "INATIVO",
  };
  const normalized = String(status || "reserved").replace(/_/g, " ").trim().toLowerCase();
  const label = statusLabelMap[normalized] || normalized.toUpperCase();

  return (
    <Chip
      label={label || "RESERVADO"}
      sx={{
        bgcolor: "rgba(37,109,255,0.10)",
        color: "#16325c",
        fontWeight: 900,
        borderRadius: 999,
        px: 1.5,
        border: "1px solid rgba(37,109,255,0.22)",
      }}
    />
  );
};

// tenta uma lista de endpoints e retorna o primeiro que responder 2xx com JSON
async function tryManyJson(paths) {
  for (const p of paths) {
    try {
      const data = await getJSON(p);
      return { data, from: p };
    } catch {}
  }
  return { data: null, from: null };
}

// POST em uma lista de endpoints, parando no primeiro 2xx
async function tryManyPost(paths, body) {
  for (const p of paths) {
    try {
      const r = await fetch(apiJoin(p), {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        credentials: "include",
        body: JSON.stringify(body || {}),
      });
      if (r.ok) return await r.json().catch(() => ({}));
    } catch {}
  }
  throw new Error("save_failed");
}

// normaliza payloads diferentes para um único formato
function normalizeToEntries(payPayload, reservationsPayload) {
  if (payPayload) {
    const list = Array.isArray(payPayload)
      ? payPayload
      : payPayload.payments || payPayload.items || [];
    return list.flatMap(p => {
      const drawId = p.draw_id ?? p.drawId ?? p.sorteio_id ?? null;
      const numbers = Array.isArray(p.numbers) ? p.numbers : [];
      const payStatus = p.status || p.paymentStatus || "pending";
      const when = p.paid_at || p.updated_at || p.created_at || null;
      return numbers.map(n => ({
        payment_id: p.id ?? p.payment_id ?? null,
        reservation_id: p.reservation_id ?? p.reservationId ?? null,
        draw_id: drawId,
        number: Number(n),
        status: String(payStatus).toLowerCase(),
        when,
        expires_at: p.expires_at || p.expire_at || null,
      }));
    });
  }

  if (reservationsPayload) {
    const list = reservationsPayload.reservations || reservationsPayload.items || [];
    return list.map(r => {
      const raw = String(r.status || "").toLowerCase();
      // mapear corretamente os estados do reservations
      let st = "pending";
      if (raw === "paid" || raw === "sold" || raw === "approved") st = "approved";
      else if (/(active|reserved|pending|await|aguard)/.test(raw)) st = "pending";
      else if (/(expired|cancel)/.test(raw)) st = "expired";
      return {
        reservation_id: r.id ?? r.reservation_id ?? null,
        draw_id: r.draw_id ?? r.sorteio_id ?? null,
        number: Number(r.n ?? r.number ?? r.numero),
        status: st,
        when: r.paid_at || r.updated_at || r.created_at || null,
        expires_at: r.reserved_until || r.expires_at || r.expire_at || null,
      };
    });
  }

  return [];
}

function getPromocionalNumbers(item) {
  if (Array.isArray(item?.numbers)) return item.numbers;
  if (Array.isArray(item?.selected_numbers)) return item.selected_numbers;
  if (Array.isArray(item?.numeros)) return item.numeros;
  if (Array.isArray(item?.reservation?.numbers)) return item.reservation.numbers;
  if (item?.numbers_label) return String(item.numbers_label).split(",");
  if (item?.reservation?.numbers_label) return String(item.reservation.numbers_label).split(",");
  if (item?.number != null) return [item.number];
  if (item?.numero != null) return [item.numero];
  if (item?.reservation?.number != null) return [item.reservation.number];
  return [];
}

function normalizePromocionalParticipationRows(participations) {
  return (participations || []).map((item, index) => {
    const draw = item?.draw || item?.promotional_draw || item?.promotionalDraw || {};
    const reservation = item?.reservation || item?.promotional_reservation || item?.promotionalReservation || {};
    const payment = item?.payment || {};
    const drawId =
      item?.draw_id ??
      item?.drawId ??
      item?.promotional_draw_id ??
      item?.promotionalDrawId ??
      draw?.draw_id ??
      draw?.drawId ??
      draw?.id ??
      draw?._id;
    const reservationId =
      item?.reservation_id ??
      item?.reservationId ??
      item?.promotional_reservation_id ??
      reservation?.reservation_id ??
      reservation?.reservationId ??
      reservation?.id ??
      reservation?._id ??
      payment?.reservation_id ??
      payment?.reservationId ??
      item?.id ??
      item?._id;
    const paymentStatus =
      item?.payment_status ??
      item?.paymentStatus ??
      payment?.status ??
      payment?.payment_status ??
      (typeof item?.payment === "string" ? item.payment : undefined) ??
      "pending";
    const reservationStatus =
      item?.status ??
      item?.reservation_status ??
      item?.reservationStatus ??
      reservation?.status ??
      "reserved";
    const numberList = getPromocionalNumbers(item);
    const numbersText = Array.isArray(item?.numbers)
      ? item.numbers.map((number) => String(number).trim()).filter(Boolean).join(", ")
      : String(item?.numbers || item?.numbers_label || numberList.join(", ") || "");
    const totalCents = Number(
      item?.total_cents ??
        item?.totalCents ??
        item?.amount_cents ??
        item?.amountCents ??
        payment?.amount_cents ??
        payment?.amountCents ??
        0
    );
    const whenValue =
      item?.created_at ||
      item?.createdAt ||
      item?.day ||
      item?.reserved_at ||
      item?.updated_at ||
      item?.date;
    const whenMs = Date.parse(whenValue || "") || 0;
    const whenDate = whenMs ? new Date(whenMs) : null;
    const title =
      item?.draw_title ||
      item?.drawTitle ||
      item?.title ||
      item?.name ||
      item?.promotional_title ||
      item?.promotionalTitle ||
      draw?.title ||
      draw?.name ||
      "Promocional";

    return {
      id: item?.id || item?._id || `promocional-${index}`,
      type: "promotional",
      source: "promotional",
      is_promotional: true,
      typeLabel: "Promocional",
      drawId,
      reservationId,
      title,
      numbers: numbersText,
      date: whenValue || null,
      paymentStatus,
      status: reservationStatus,
      totalCents: Number.isFinite(totalCents) ? totalCents : 0,
      reservation_id: reservationId,
      draw_id: drawId,
      sorteio: title,
      numeros: numberList,
      numbers_label: numbersText,
      dia: item?.day || (whenDate ? whenDate.toLocaleDateString("pt-BR") : "--/--/----"),
      payment_status: paymentStatus,
      pagamento: paymentStatus,
      resultado: reservationStatus,
      whenMs,
      amount_cents: Number.isFinite(totalCents) ? totalCents : 0,
      payment_id: item?.payment_id || item?.paymentId || payment?.id || payment?.payment_id || null,
      can_pay:
        ["pending", "waiting", "created"].includes(
          String(paymentStatus || "pending").toLowerCase()
        ) &&
        ["reserved", "pending", "active"].includes(
          String(reservationStatus || "reserved").toLowerCase()
        ),
      canPay:
        ["pending", "waiting", "created"].includes(
          String(paymentStatus || "pending").toLowerCase()
        ) &&
        ["reserved", "pending", "active"].includes(
          String(reservationStatus || "reserved").toLowerCase()
        ),
      raw: item,
    };
  });
}

function getReservationPayloadList(payload) {
  if (Array.isArray(payload)) return payload;
  return payload?.reservations || payload?.items || payload?.data?.reservations || payload?.data || [];
}

function getReservationNumbers(item) {
  if (Array.isArray(item?.numbers)) return item.numbers;
  if (Array.isArray(item?.numeros)) return item.numeros;
  if (Array.isArray(item?.selected_numbers)) return item.selected_numbers;
  if (Array.isArray(item?.reservation?.numbers)) return item.reservation.numbers;
  if (item?.numbers_label) return String(item.numbers_label).split(",");
  if (item?.number != null) return [item.number];
  if (item?.numero != null) return [item.numero];
  if (item?.n != null) return [item.n];
  return [];
}

function normalizeUnifiedReservationRows(payload, drawsMap = new Map()) {
  return getReservationPayloadList(payload).map((item, index) => {
    const draw = item?.draw || item?.promotional_draw || item?.promotionalDraw || {};
    const reservation = item?.reservation || {};
    const payment = item?.payment || {};
    const typeRaw = String(
      item?.type ||
        item?.source ||
        item?.reservation_type ||
        item?.draw_type ||
        item?.kind ||
        ""
    ).toLowerCase();
    const isPromotional =
      typeRaw === "promotional" ||
      typeRaw === "promocional" ||
      item?.is_promotional === true ||
      item?.promotional === true ||
      item?.promotional_draw_id != null ||
      item?.promotionalDrawId != null;

    const drawId =
      item?.draw_id ??
      item?.drawId ??
      item?.promotional_draw_id ??
      item?.promotionalDrawId ??
      reservation?.draw_id ??
      reservation?.drawId ??
      draw?.draw_id ??
      draw?.drawId ??
      draw?.id ??
      draw?._id;
    const reservationId =
      item?.reservation_group_id ??
      item?.reservationGroupId ??
      item?.reservation_id ??
      item?.reservationId ??
      reservation?.reservation_group_id ??
      reservation?.reservationGroupId ??
      reservation?.reservation_id ??
      reservation?.reservationId ??
      reservation?.id ??
      reservation?._id ??
      item?.id ??
      item?._id;
    const numbers = getReservationNumbers(item);
    const numbersLabel =
      item?.numbers_label ||
      numbers.map((number) => String(number).trim()).filter(Boolean).join(", ");
    const status =
      item?.status ??
      item?.reservation_status ??
      item?.reservationStatus ??
      reservation?.status ??
      "reserved";
    const paymentStatus =
      item?.payment_status ??
      item?.paymentStatus ??
      payment?.payment_status ??
      payment?.status ??
      item?.pagamento ??
      (/^(paid|approved|pago|sold)$/i.test(String(status || "")) ? "paid" : "pending");
    const whenValue =
      item?.created_at ||
      item?.createdAt ||
      item?.reserved_at ||
      item?.updated_at ||
      item?.date ||
      item?.day;
    const whenMs = Date.parse(whenValue || "") || 0;
    const whenDate = whenMs ? new Date(whenMs) : null;
    const title =
      item?.draw_title ||
      item?.drawTitle ||
      item?.title ||
      item?.name ||
      item?.promotional_title ||
      item?.promotionalTitle ||
      draw?.title ||
      draw?.name ||
      (isPromotional ? "Promocional" : (drawId != null ? String(drawId) : "--"));
    const amountCents = Number(
      item?.total_cents ??
        item?.totalCents ??
        item?.amount_cents ??
        item?.amountCents ??
        payment?.amount_cents ??
        payment?.amountCents ??
        0
    );
    const expired = isReservationExpired(item) || isReservationExpired(reservation);
    const paid =
      isPaidStatus(paymentStatus) || isPaidStatus(status);
    const paymentStatusNorm = paid ? "paid" : String(paymentStatus || "pending").toLowerCase();
    const pendingPayment = ["pending", "reserved", "waiting", "created"].includes(
      String(paymentStatus || "pending").toLowerCase()
    );

    return {
      id: item?.id || item?._id || `${isPromotional ? "promotional" : "normal"}-${index}`,
      type: isPromotional ? "promotional" : "normal",
      source: isPromotional ? "promotional" : "normal",
      is_promotional: isPromotional,
      typeLabel: isPromotional ? "Promocional" : "Principal",
      drawId,
      reservationId,
      title,
      numbers: numbersLabel,
      date: whenValue || null,
      paymentStatus: paymentStatusNorm,
      status: paid ? "paid" : status,
      totalCents: Number.isFinite(amountCents) ? amountCents : 0,
      reservation_id: reservationId,
      draw_id: drawId,
      draw_number:
        item?.draw_number ??
        draw?.draw_number ??
        draw?.number ??
        drawId,
      draw_title: title,
      sorteio: formatDrawLabel({
        draw_number:
          item?.draw_number ?? draw?.draw_number ?? draw?.number ?? drawId,
        draw_title: title,
        draw_id: drawId,
      }),
      numeros: numbers,
      numbers_label: numbersLabel,
      dia: item?.day || (whenDate ? whenDate.toLocaleDateString("pt-BR") : "--/--/----"),
      payment_status: paymentStatusNorm,
      pagamento: paymentStatusNorm,
      expires_at:
        item?.expires_at ||
        item?.reserved_until ||
        item?.expire_at ||
        reservation?.expires_at ||
        reservation?.reserved_until ||
        null,
      resultado: isPromotional
        ? (paid ? "paid" : status)
        : (drawsMap.get(Number(drawId)) || status || "aberto"),
      whenMs,
      amount_cents: Number.isFinite(amountCents) ? amountCents : 0,
      payment_id: item?.payment_id || item?.paymentId || payment?.id || payment?.payment_id || null,
      can_pay: isPromotional
        ? pendingPayment &&
          ["reserved", "pending", "active"].includes(String(status || "reserved").toLowerCase()) &&
          !expired &&
          !paid
        : pendingPayment && !expired && !paid,
      canPay: isPromotional
        ? pendingPayment &&
          ["reserved", "pending", "active"].includes(String(status || "reserved").toLowerCase()) &&
          !expired &&
          !paid
        : pendingPayment && !expired && !paid,
      raw: item,
    };
  });
}

// parse JSON tolerante
async function fetchJsonLoose(url, options) {
  const r = await fetch(apiJoin(url), options);
  if (!r.ok) return null;
  try {
    return await r.json();
  } catch {
    try {
      const txt = await r.text();
      const cleaned = String(txt).trim().replace(/^[^{[]*/, "");
      return JSON.parse(cleaned);
    } catch {
      return null;
    }
  }
}

// ▸▸ helpers para sincronizar o cupom com novas compras
function asTime(v) {
  const t = Date.parse(v || "");
  return Number.isFinite(t) ? t : 0;
}

function reservationExpiresMs(item) {
  return (
    asTime(item?.expires_at) ||
    asTime(item?.reserved_until) ||
    asTime(item?.expire_at) ||
    0
  );
}

function isReservationExpired(item) {
  const exp = reservationExpiresMs(item);
  return exp > 0 && exp < Date.now();
}

async function syncCouponBalance() {
  return await tryManyPost(["/coupons/sync", "/me/coupons/sync"], {});
}

export default function AccountPage() {
  const navigate = useNavigate();
  React.useContext(SelectionContext);
  const { logout, user: ctxUser } = useAuth();

  const [menuEl, setMenuEl] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [loadingProfile, setLoadingProfile] = React.useState(false);
  const [loadingReservations, setLoadingReservations] = React.useState(true);
  const [loadingCoupon, setLoadingCoupon] = React.useState(true);
  const [user, setUser] = React.useState(ctxUser || null);
  const [rows, setRows] = React.useState([]);
  const [promotionalError, setPromotionalError] = React.useState("");
  const accountLoadingRef = React.useRef(false);

  // ► saldo composto
  const [, setBaseCents] = React.useState(0); // pode incluir safeUi p/ nunca regredir visualmente
  const [, setPaidCents] = React.useState(0); // soma payments approved (não usado na UI)

  // Valor oficial vindo do servidor (coupon_value_cents)
  const [officialCents, setOfficialCents] = React.useState(0);
  const valorAcumulado = (Number(officialCents) || 0) / 100;

  const [cupom, setCupom] = React.useState("CUPOMAQUI");
  const [validade, setValidade] = React.useState("--/--/--");

  const [purchaseHistory, setPurchaseHistory] = React.useState([]);
  const [lastPurchase, setLastPurchase] = React.useState(null);
  const [purchaseHistoryLoading, setPurchaseHistoryLoading] = React.useState(true);
  const [purchaseHistoryError, setPurchaseHistoryError] = React.useState("");

  // estado das configurações (apenas admin)
  const [cfgLoading, setCfgLoading] = React.useState(false);
  const [cfgSaved, setCfgSaved] = React.useState(null);
  const [cfg, setCfg] = React.useState({
    banner_title: "",
    max_numbers_per_selection: 5,
  });

  // ▼ PIX
  const [pixOpen, setPixOpen] = React.useState(false);
  const [pixLoading, setPixLoading] = React.useState(false);
  const [pixData, setPixData] = React.useState(null);
  const [pixAmount, setPixAmount] = React.useState(null);
  const [pixMsg, setPixMsg] = React.useState("");
  const [loadingPixId, setLoadingPixId] = React.useState(null);

  // AutoPay
  const [autoOpen, setAutoOpen] = React.useState(false);
  const [claims, setClaims] = React.useState({ taken: [], mine: [] });
  const loadClaims = React.useCallback(async () => {
    try {
      const j = await getJSON("/autopay/claims");
      setClaims({
        taken: Array.isArray(j?.taken) ? j.taken : [],
        mine: Array.isArray(j?.mine) ? j.mine : [],
      });
    } catch {}
  }, []);
  React.useEffect(() => { loadClaims(); }, [loadClaims]);
  const handleCloseAutoModal = React.useCallback(async () => {
    await loadClaims();
    setAutoOpen(false);
  }, [loadClaims]);

  // NOVO: busca a ÚLTIMA reserva ATIVA do sorteio, priorizando os números informados
  async function findLatestActiveReservation(drawId, numbersHint) {
    const want = new Set(
      Array.isArray(numbersHint)
        ? numbersHint.map(n => Number(n))
        : Number.isFinite(Number(numbersHint)) ? [Number(numbersHint)] : []
    );

    const endpoints = [
      "/me/reservations?active=1",
      "/me/reservations",
      "/reservations/me?active=1",
      "/reservations/me",
    ];

    let best = null; // { id, number, when }

    for (const base of endpoints) {
      const url = `${base}${base.includes("?") ? "&" : "?"}_=${Date.now()}`;
      try {
        const r = await fetch(apiJoin(url), {
          headers: { "Content-Type": "application/json", ...authHeaders() },
          credentials: "include",
          cache: "no-store",
        });
        if (!r.ok) continue;

        const j = await r.json().catch(() => ({}));
        const list = Array.isArray(j) ? j : (j.reservations || j.items || []);

        for (const x of list || []) {
          const d = Number(x?.draw_id ?? x?.sorteio_id);
          if (d !== Number(drawId)) continue;

          if (isReservationExpired(x)) continue;

          const raw = String(x?.status || "").toLowerCase();
          const isActive = /(active|reserved|pending|await|aguard)/.test(raw) && !/(expired|cancel)/.test(raw);
          if (!isActive) continue;

          const nums = Array.isArray(x?.numbers)
            ? x.numbers.map(Number)
            : [Number(x?.n ?? x?.number ?? x?.numero)].filter(Number.isFinite);

          const candidates = want.size ? nums.filter(n => want.has(n)) : nums;
          if (!candidates.length) continue;

          const when = asTime(x?.updated_at) || asTime(x?.created_at) || asTime(x?.reserved_until) || 0;
          for (const n of candidates) {
            if (!best || when > best.when) {
              best = {
                id:
                  x.reservation_group_id ||
                  x.reservationGroupId ||
                  x.reservation_id ||
                  x.id ||
                  x.reservationId,
                number: n,
                when,
              };
            }
          }
        }
      } catch {}
      if (best) break; // já achou uma ativa recente neste endpoint
    }

    return best ? { reservationId: best.id, number: best.number } : null;
  }

  // Procura payment pendente p/ (drawId, number)
  async function findPendingPayment(drawId, number) {
    try {
      const url = `/payments/me?_=${Date.now()}`;
      const r = await fetch(apiJoin(url), {
        headers: { "Content-Type": "application/json", ...authHeaders() },
        credentials: "include",
        cache: "no-store",
      });
      if (!r.ok) return null;
      const j = await r.json().catch(() => ({}));
      const list = Array.isArray(j) ? j : (j.payments || j.items || []);
      return (list || []).find(p => {
        const d = Number(p?.draw_id ?? p?.drawId ?? p?.sorteio_id);
        const ns = Array.isArray(p?.numbers) ? p.numbers.map(n => Number(n)) : [];
        const status = String(p?.status || "").toLowerCase();
        return d === Number(drawId) && ns.includes(Number(number)) && status === "pending";
      }) || null;
    } catch {
      return null;
    }
  }

  // --------- GERAR PIX ----------
  async function handleGeneratePix(row) {
    console.log("[PIX_ITEM]", row);
    setPixMsg("Gerando PIX…");
    setPixOpen(true);
    setPixLoading(true);
    setPixData(null);
    setLoadingPixId(
      row?.reservationId ||
        row?.reservation_id ||
        row?.id ||
        `${row?.type || "main"}-${row?.drawId || row?.draw_id || row?.sorteio}`
    );

    try {
      const isPromotional =
        row?.type === "promotional" ||
        row?.source === "promotional" ||
        row?.is_promotional === true;

      if (isPromotional) {
        const reservationId = row?.reservationId || row?.reservation_id;

        if (!reservationId) {
          throw new Error("Dados da reserva promocional incompletos para gerar PIX.");
        }

        const drawId =
          row?.drawId ||
          row?.draw_id ||
          row?.promotional_draw_id ||
          row?.promotionalDrawId;

        const created = drawId
          ? await generatePromocionalPix(drawId, reservationId)
          : await generatePromocionalPix(reservationId);
        console.log("[PIX_SUCCESS_PROMOTIONAL]", created);

        const pix = normalizePixData(created);
        setPixData(pix);

        const cents = Number(
          pix?.amount_cents ??
            pix?.amountCents ??
            created?.amount_cents ??
            created?.amountCents ??
            row?.amount_cents
        );

        const amount = Number(pix?.amount ?? created?.amount);

        setPixAmount(
          Number.isFinite(cents) && cents > 0
            ? cents / 100
            : Number.isFinite(amount) && amount > 0
              ? amount
              : null
        );

        setPixMsg(pix?.status ? `Status: ${pix.status}` : "PIX promocional gerado.");
        return;
      }

      const drawId = Number(row?.draw_id ?? row?.sorteio ?? row?.draw ?? row?.id);

      const rowReservationId =
        row?.reservation_group_id ||
        row?.reservationGroupId ||
        row?.reservation_id ||
        row?.reservationId;

      if (rowReservationId) {
        try {
          const created = await generateMainReservationPix(rowReservationId);
          console.log("[PIX_SUCCESS]", created);
          const pix = normalizePixData(created);
          setPixData(pix);
          const cents =
            pix?.amount_cents ??
            pix?.amountCents ??
            created?.amount_cents ??
            created?.amountCents ??
            row?.amount_cents;
          setPixAmount(typeof cents === "number" ? cents / 100 : pix?.amount || null);
          setPixMsg(pix?.status ? `Status: ${pix.status}` : "PIX criado.");
          return;
        } catch (error) {
          console.warn("[AccountPage] generateMainReservationPix fallback:", error);
        }
      }

      // ► Prioriza a ÚLTIMA reserva ATIVA dentro dos números exibidos na linha
      const hintNumbers = Array.isArray(row?.numeros)
        ? row.numeros
        : (Number.isFinite(Number(row?.number ?? row?.numero ?? row?.num))
            ? [Number(row?.number ?? row?.numero ?? row?.num)]
            : []);

      const latest = await findLatestActiveReservation(drawId, hintNumbers);
      if (!latest) {
        setPixMsg("Falha ao gerar PIX: sua reserva não está ativa. Volte ao sorteio para reservar novamente.");
        return;
      }

      let selectedNumber = Number(latest.number);

      // Reaproveita pagamento pendente existente (para o número correto)
      const already = await findPendingPayment(drawId, selectedNumber);
      if (already && (already.qr_code || already.qr_code_base64 || already.copy || already.copy_paste)) {
        setPixData(already);
        const cents = already?.amount_cents ?? null;
        setPixAmount(typeof cents === "number" ? cents / 100 : null);
        setPixMsg(already?.status ? `Status: ${already.status}` : `PIX pendente do nº ${pad2(selectedNumber)} recuperado.`);
        return;
      }

      let created = null;
      try {
        created = await generateMainReservationPix(latest.reservationId);
      } catch (pixErr) {
        const msg = String(pixErr?.message || "");
        if (/reservation[_\s-]?not[_\s-]?active|expired|inativa|expirada/i.test(msg)) {
          const fresh = await findLatestActiveReservation(drawId, hintNumbers);
          if (fresh && fresh.reservationId !== latest.reservationId) {
            selectedNumber = Number(fresh.number);
            created = await generateMainReservationPix(fresh.reservationId);
          } else {
            setPixMsg("Falha ao gerar PIX: sua reserva não está ativa. Volte ao sorteio para reservar novamente.");
            return;
          }
        } else {
          throw pixErr;
        }
      }

      if (!created) return;

      console.log("[PIX_SUCCESS]", created);
      setPixData(
        normalizePixData({
          ...created,
          reservation_id: created.reservation_id || latest.reservationId,
        })
      );

      // Descobre o valor (centavos)
      let amountCents =
        (typeof created?.amount_cents === "number" && created.amount_cents) ||
        (typeof created?.payment?.amount_cents === "number" && created.payment.amount_cents) ||
        null;

      if (amountCents == null) {
        const nowPending = await findPendingPayment(drawId, selectedNumber);
        if (nowPending?.amount_cents != null) amountCents = nowPending.amount_cents;
      }
      if (amountCents == null) {
        const id = created?.paymentId || created?.id || created?.txid || created?.e2eid;
        if (id) {
          try {
            const det = await checkPixStatus(id);
            if (det?.amount_cents != null) amountCents = det.amount_cents;
          } catch {}
        }
      }

      setPixAmount(amountCents != null ? amountCents / 100 : null);
      setPixMsg(created?.status ? `Status: ${created.status}` : `PIX criado para o nº ${pad2(selectedNumber)}.`);
    } catch (e) {
      console.error("[GENERATE_PIX_ERROR]", {
        item: row,
        error: e,
        message: e?.message,
      });
      setPixData(null);
      setPixMsg(e?.message || "Não foi possível gerar o PIX. Tente novamente.");
    } finally {
      setPixLoading(false);
      setLoadingPixId(null);
    }
  }

  function copyPix() {
    const key = pixData?.copy || pixData?.copy_paste || pixData?.copy_paste_code || pixData?.emv || pixData?.qr_code || "";
    if (key) navigator.clipboard.writeText(key).catch(() => {});
  }

  function normalizePixData(payload) {
    const source =
      payload?.payment ||
      payload?.pix ||
      payload?.data?.payment ||
      payload?.data?.pix ||
      payload?.data ||
      payload ||
      {};
    return {
      ...source,
      paymentId: source.paymentId || source.payment_id || source.id || payload?.paymentId || payload?.id,
      payment_id: source.payment_id || source.paymentId || source.id || payload?.payment_id || payload?.paymentId || payload?.id,
      reservation_id:
        source.reservation_id ||
        source.reservationId ||
        payload?.reservation_id ||
        payload?.reservationId,
      qr_code:
        source.qr_code ||
        source.pix_qr_code ||
        source.copy_paste_code ||
        source.copy_paste ||
        source.copy ||
        payload?.qr_code ||
        payload?.pix_qr_code,
      copy_paste_code:
        source.copy_paste_code ||
        source.qr_code ||
        source.pix_qr_code ||
        source.copy_paste ||
        source.copy ||
        payload?.copy_paste_code ||
        payload?.qr_code ||
        payload?.pix_qr_code,
      qr_code_base64: source.qr_code_base64 || source.pix_qr_code_base64 || payload?.qr_code_base64 || payload?.pix_qr_code_base64,
      ticket_url: source.ticket_url || source.pix_ticket_url || payload?.ticket_url || payload?.pix_ticket_url,
      amount_cents: source.amount_cents ?? source.amountCents ?? payload?.amount_cents ?? payload?.amountCents,
      amount: source.amount ?? payload?.amount,
      status: source.status || source.payment_status || payload?.status || payload?.payment_status || "pending",
    };
  }

  const doLogout = () => { setMenuEl(null); logout(); navigate("/"); };
  const storedMe = React.useMemo(() => {
    try { return JSON.parse(localStorage.getItem("me") || "null"); } catch { return null; }
  }, []);

  const applyCouponFromMine = React.useCallback((mine) => {
    const currentCents =
      Number(mine?.cents ?? mine?.coupon_value_cents ?? mine?.value_cents ?? 0) || 0;
    const code = mine?.code || mine?.coupon_code || null;

    setOfficialCents(Number.isFinite(currentCents) && currentCents >= 0 ? currentCents : 0);
    if (Number.isFinite(currentCents) && currentCents >= 0) setBaseCents(currentCents);
    if (code) setCupom(String(code));
    setPaidCents(0);
  }, []);

  const loadCoupon = React.useCallback(async () => {
    setLoadingCoupon(true);
    try {
      const mine = await fetchJsonLoose("/coupons/mine", {
        headers: { ...authHeaders() },
        credentials: "include",
      });
      applyCouponFromMine(mine);
    } catch (e) {
      console.warn("[loadCoupon]", e?.message || e);
    } finally {
      setLoadingCoupon(false);
    }
  }, [applyCouponFromMine]);

  const syncAndReloadCoupon = React.useCallback(async () => {
    setLoadingCoupon(true);
    try {
      await syncCouponBalance();
      const mine = await fetchJsonLoose("/coupons/mine", {
        headers: { ...authHeaders() },
        credentials: "include",
      });
      applyCouponFromMine(mine);
    } catch (e) {
      console.warn("[syncAndReloadCoupon]", e?.message || e);
    } finally {
      setLoadingCoupon(false);
    }
  }, [applyCouponFromMine]);

  const loadProfile = React.useCallback(async () => {
    setLoadingProfile(true);
    try {
      let me = ctxUser || storedMe || null;
      try {
        const meResp = await getJSON("/me");
        me = meResp?.user || meResp || me;
      } catch {}
      setUser(me || null);
      try {
        if (me) localStorage.setItem("me", JSON.stringify(me));
      } catch {}
    } finally {
      setLoadingProfile(false);
    }
  }, [ctxUser, storedMe]);

  const loadPurchaseHistory = React.useCallback(async () => {
    setPurchaseHistoryLoading(true);
    setPurchaseHistoryError("");
    try {
      const historyPayload = await getJSON("/me/purchase-history");
      const normalizedHistory = normalizePurchaseHistoryPayload(historyPayload);
      setPurchaseHistory(normalizedHistory.items);
      setLastPurchase(normalizedHistory.lastPurchase);
    } catch (historyError) {
      console.error("[PURCHASE_HISTORY_ERROR]", historyError);
      setPurchaseHistory([]);
      setLastPurchase(null);
      setPurchaseHistoryError("Não foi possível carregar o histórico de compras.");
    } finally {
      setPurchaseHistoryLoading(false);
    }
  }, []);

  const loadReservations = React.useCallback(async () => {
    setLoadingReservations(true);
    setPromotionalError("");
    try {
      const { data: pay, from } = await tryManyJson([
        "/me/reservations",
        "/me/reservations?active=1",
        "/reservations/me",
        "/reservations/me?active=1",
        "/payments/me",
      ]);

      const drawMetaById = new Map();
      let drawsMap = new Map();
      try {
        const draws = await getJSON("/draws");
        const arr = Array.isArray(draws) ? draws : (draws.draws || draws.items || []);
        for (const d of arr) {
          const id = Number(d.id ?? d.draw_id);
          drawsMap.set(id, d.status ?? d.result ?? "");
          drawMetaById.set(id, {
            number: d.draw_number ?? d.number ?? id,
            title: d.title ?? d.name ?? d.banner_title ?? "Sorteio Principal",
          });
        }
      } catch {}

      let mainRows = [];
      let unifiedRows = [];

      if (pay && from !== "/payments/me") {
        unifiedRows = normalizeUnifiedReservationRows(pay, drawsMap);
        mainRows = unifiedRows
          .filter((row) => row.type !== "promotional")
          .map((row) => ({
            ...row,
            sorteio: formatDrawLabel(row),
          }));
      } else if (pay) {
        const entries = normalizeToEntries(
          from === "/payments/me" ? pay : null,
          from !== "/payments/me" ? pay : null
        );

        const now = Date.now();
        const ttlMs = TTL_MINUTES * 60 * 1000;

        const filtered = entries.filter((e) => {
          const st = String(e.status || "").toLowerCase();
          if (["approved", "paid", "pago"].includes(st)) return true;
          if (e.expires_at) {
            const expMs = new Date(e.expires_at).getTime();
            if (!isNaN(expMs)) return expMs > now;
          }
          if (e.when) {
            const whenMs = new Date(e.when).getTime();
            if (!isNaN(whenMs)) return whenMs + ttlMs > now;
          }
          return true;
        });

        const byKey = new Map();
        const priority = (st) => {
          const s = String(st || "").toLowerCase();
          if (["approved", "paid", "pago"].includes(s)) return 2;
          if (/(expired|cancel)/.test(s)) return 0;
          return 1;
        };
        for (const e of filtered) {
          const key = `${Number(e.draw_id)}|${Number(e.number)}`;
          const cur = byKey.get(key);
          if (!cur) {
            byKey.set(key, e);
            continue;
          }
          const pNew = priority(e.status);
          const pOld = priority(cur.status);
          if (pNew > pOld) byKey.set(key, e);
          else if (pNew === pOld) {
            const tNew = e.when ? new Date(e.when).getTime() : 0;
            const tOld = cur.when ? new Date(cur.when).getTime() : 0;
            if (tNew >= tOld) byKey.set(key, e);
          }
        }
        const deduped = Array.from(byKey.values());

        const byDraw = new Map();
        const isPendingStatus = (s) =>
          /pending|pendente|await|aguard|active|ativo|reserv/.test(
            String(s || "").toLowerCase()
          );
        const isApprovedStatus = (s) =>
          /^(approved|paid|pago)$/.test(String(s || "").toLowerCase());

        for (const e of deduped) {
          const id = Number(e.draw_id);
          if (!byDraw.has(id)) {
            byDraw.set(id, {
              draw_id: id,
              reservation_id: e.reservation_id || null,
              numeros: [],
              when: e.when ? new Date(e.when).getTime() : 0,
              hasPending: false,
              hasApproved: false,
            });
          }
          const g = byDraw.get(id);
          if (!g.reservation_id && e.reservation_id) g.reservation_id = e.reservation_id;
          g.numeros.push(Number(e.number));
          g.when = Math.max(g.when, e.when ? new Date(e.when).getTime() : 0);
          g.hasPending = g.hasPending || isPendingStatus(e.status);
          g.hasApproved = g.hasApproved || isApprovedStatus(e.status);
        }

        mainRows = Array.from(byDraw.values()).map((g) => {
          const meta = drawMetaById.get(Number(g.draw_id)) || {};
          const drawStatus = drawsMap.get(Number(g.draw_id)) || "aberto";
          const whenDate = g.when ? new Date(g.when) : null;
          const pagamento = g.hasPending ? "pending" : g.hasApproved ? "paid" : "pending";
          const paid = isPaidStatus(pagamento);
          return {
            type: "main",
            source: "normal",
            typeLabel: "Principal",
            reservation_id: g.reservation_id,
            draw_id: g.draw_id,
            draw_number: meta.number ?? g.draw_id,
            draw_title: meta.title ?? "Sorteio Principal",
            sorteio: formatDrawLabel({
              draw_number: meta.number ?? g.draw_id,
              draw_title: meta.title ?? "Sorteio Principal",
            }),
            numeros: Array.from(new Set(g.numeros)).sort((a, b) => a - b),
            dia: whenDate ? whenDate.toLocaleDateString("pt-BR") : "--/--/----",
            pagamento,
            payment_status: pagamento,
            paymentStatus: pagamento,
            resultado: drawStatus || "aberto",
            whenMs: g.when || 0,
            canPay: !paid,
            can_pay: !paid,
          };
        });
      }

      let promotionalRows = unifiedRows.filter((row) => row.type === "promotional");
      try {
        const promotionalReservations = await getMyPromocionalReservations();
        const fetchedPromotionalRows =
          normalizePromocionalParticipationRows(promotionalReservations);
        const promotionalByKey = new Map();

        [...promotionalRows, ...fetchedPromotionalRows].forEach((row, index) => {
          const key =
            row?.reservationId ||
            row?.reservation_id ||
            `${row?.drawId || row?.draw_id || "promotional"}-${row?.numbers || row?.numbers_label || index}`;
          promotionalByKey.set(String(key), {
            ...row,
            sorteio: formatDrawLabel(row),
          });
        });

        promotionalRows = Array.from(promotionalByKey.values());
      } catch (error) {
        console.error("[PROMOCIONAL_FRONT_ERROR]", error);
        if (!promotionalRows.length) {
          setPromotionalError("Não foi possível carregar participações promocionais.");
        }
      }

      setRows(
        [...mainRows, ...promotionalRows].sort((a, b) => (b.whenMs || 0) - (a.whenMs || 0))
      );

      if (pay) {
        let lastApprovedAtMs = null;
        const listForValidity =
          from === "/payments/me"
            ? Array.isArray(pay)
              ? pay
              : pay.payments || []
            : [];

        for (const p of listForValidity) {
          const st = String((p.status ?? p?.status)?.toString() || "").toLowerCase();
          const ok = st === "approved" || st === "paid" || st === "pago";
          const t = Date.parse(p.paid_at || p.when || p.updated_at || p.created_at || "");
          if (ok && !isNaN(t)) lastApprovedAtMs = Math.max(lastApprovedAtMs ?? 0, t);
        }

        if (lastApprovedAtMs) {
          const exp = new Date(
            lastApprovedAtMs + COUPON_VALIDITY_DAYS * 24 * 60 * 60 * 1000
          );
          const yy = String(exp.getFullYear()).slice(-2);
          setValidade(`${pad2(exp.getDate())}/${pad2(exp.getMonth() + 1)}/${yy}`);
        } else {
          setValidade("--/--/--");
        }
      }
    } catch (loadError) {
      console.warn("[loadReservations]", loadError?.message || loadError);
      setRows([]);
    } finally {
      setLoadingReservations(false);
    }
  }, []);

  const safeLoadAccount = React.useCallback(
    async ({ syncCoupon = false } = {}) => {
      if (accountLoadingRef.current) return;
      accountLoadingRef.current = true;
      setLoading(true);

      try {
        const tasks = [
          loadProfile(),
          loadPurchaseHistory(),
          loadReservations(),
          syncCoupon ? syncAndReloadCoupon() : loadCoupon(),
        ];
        await Promise.allSettled(tasks);
      } finally {
        accountLoadingRef.current = false;
        setLoading(false);
      }
    },
    [loadProfile, loadPurchaseHistory, loadReservations, loadCoupon, syncAndReloadCoupon]
  );

  async function refreshPix() {
    const paymentId =
      pixData?.id || pixData?.paymentId || pixData?.payment_id || pixData?.txid;
    if (!paymentId) return;

    try {
      const next = await checkPixStatus(paymentId);

      setPixData((prev) => ({
        ...(prev || {}),
        ...(next || {}),
      }));

      const status = next?.status || next?.payment_status;

      if (next?.status) {
        setPixMsg(`Status: ${next.status}`);
      }

      if (typeof next?.amount_cents === "number") {
        setPixAmount(next.amount_cents / 100);
      }

      if (isPaidStatus(status)) {
        setPixMsg("Pagamento aprovado! Atualizando seus dados...");

        await safeLoadAccount({ syncCoupon: true });

        setTimeout(() => {
          setPixOpen(false);
          setPixData(null);
          setPixAmount(null);
        }, 700);
      }

      return next;
    } catch (err) {
      console.error("[ACCOUNT_PIX_REFRESH_ERROR]", err);
      alert(err?.message || "Não foi possível verificar o pagamento.");
    }
  }

  React.useEffect(() => {
    safeLoadAccount();
  }, [safeLoadAccount]);

  React.useEffect(() => {
    const onReload = () => safeLoadAccount();
    window.addEventListener("xnamai:pix-approved", onReload);
    return () => window.removeEventListener("xnamai:pix-approved", onReload);
  }, [safeLoadAccount]);

  // carregar config (banner_title e max_numbers_per_selection)
  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const j = await getJSON("/config");
        const banner = typeof j?.banner_title === "string" ? j.banner_title : "";
        const maxSel = Number(j?.max_numbers_per_selection ?? j?.max_select ?? 5);
        if (alive) setCfg({
          banner_title: banner,
          max_numbers_per_selection: Number.isFinite(maxSel) && maxSel > 0 ? maxSel : 5,
        });
      } catch {}
    })();
    return () => { alive = false; };
  }, []);

  const u = user || {};
  const headingName =
    u.name || u.fullName || u.nome || u.displayName || u.username || u.email || "NOME DO CLIENTE";
  const couponCode = u?.coupon_code || cupom || "CUPOMAQUI";
  const isAdminUser = !!(u?.is_admin || u?.role === "admin" || (u?.email && u.email.toLowerCase() === ADMIN_EMAIL));

  // salvar config
  async function handleSaveConfig() {
    try {
      setCfgLoading(true);
      setCfgSaved(null);
      const payload = {
        banner_title: String(cfg.banner_title || "").slice(0, 240),
        max_numbers_per_selection: Math.max(1, Number(cfg.max_numbers_per_selection || 1)),
      };
      await tryManyPost(
        ["/config", "/admin/config", "/config/update"],
        payload
      );
      setCfgSaved("ok");
    } catch {
      setCfgSaved("err");
    } finally {
      setCfgLoading(false);
      setTimeout(() => setCfgSaved(null), 4000);
    }
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box className="xn-accountPage">
        <Box className="xn-accountPageContent">
          <AppBar position="fixed" elevation={0} className="xn-accountHeader" sx={{ color: "#16325c" }}>
            <Toolbar sx={{ position: "relative", minHeight: { xs: 68, md: 68 }, px: { xs: 1, sm: 2 } }}>
              <IconButton edge="start" onClick={() => navigate(-1)} aria-label="Voltar" sx={{ color: "inherit" }}>
                <ArrowBackIosNewRoundedIcon />
              </IconButton>
              <Box
                component={RouterLink}
                to="/"
                sx={{
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  transform: "translate(-50%, -50%)",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <BrandLogo size={52} />
              </Box>
              <IconButton sx={{ ml: "auto", color: "inherit" }} onClick={(e) => setMenuEl(e.currentTarget)} aria-label="Menu do usuário">
                <AccountCircleRoundedIcon />
              </IconButton>
              <Menu
                anchorEl={menuEl}
                open={Boolean(menuEl)}
                onClose={() => setMenuEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                {isAdminUser && <MenuItem onClick={() => { setMenuEl(null); navigate("/admin"); }}>Painel Admin</MenuItem>}
                {isAdminUser && <Divider />}
                <MenuItem onClick={() => { setMenuEl(null); navigate("/conta"); }}>Área do cliente</MenuItem>
                <Divider />
                <MenuItem onClick={doLogout}>Sair</MenuItem>
              </Menu>
            </Toolbar>
          </AppBar>

          <Container maxWidth={false} disableGutters className="xn-accountContainer">
            <Box sx={{ height: 68 }} />
            <Stack spacing={{ xs: 2, md: 2.5 }}>
              <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
                <Typography className="xn-sectionTitle" sx={{ fontSize: { xs: 20, md: 26 }, lineHeight: 1.15 }}>
                  MINHA CONTA
                </Typography>
                <Typography className="xn-muted" sx={{ fontWeight: 700 }}>
                  {headingName}
                </Typography>
              </Box>

          {/* Configurações do sorteio (apenas admin) */}
          {isAdminUser && (
            <Paper className="xn-card" variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
              <Stack spacing={2}>
                <Typography variant="h6" className="xn-sectionTitle">Configurações do sorteio</Typography>

                <TextField
                  label="Título do banner (página principal)"
                  value={cfg.banner_title}
                  onChange={(e) => setCfg(s => ({ ...s, banner_title: e.target.value }))}
                  fullWidth
                  inputProps={{ maxLength: 240 }}
                />

                <TextField
                  label="Máx. de números por seleção"
                  type="number"
                  value={cfg.max_numbers_per_selection}
                  onChange={(e) =>
                    setCfg(s => ({ ...s, max_numbers_per_selection: Math.max(1, Number(e.target.value || 1)) }))
                  }
                  inputProps={{ min: 1, step: 1 }}
                  sx={{ maxWidth: 260 }}
                />

                <Stack direction="row" spacing={1.5}>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleSaveConfig}
                    disabled={cfgLoading}
                    className="xn-btnPrimary"
                  >
                    {cfgLoading ? "Salvando…" : "Salvar configurações"}
                  </Button>
                </Stack>

                {cfgSaved === "ok" && (
                  <Alert severity="success" variant="outlined">Configurações salvas com sucesso.</Alert>
                )}
                {cfgSaved === "err" && (
                  <Alert severity="error" variant="outlined">Não foi possível salvar. Tente novamente.</Alert>
                )}
              </Stack>
            </Paper>
          )}

              {/* Seção 1 — Cartão / Cupom / Resumo */}
              <Paper className="xn-card xn-heroCard" variant="outlined" sx={{ p: { xs: 2, md: 2.5 } }}>
                <Box className="xn-heroGrid">
                  <Box className="xn-virtualCard">
                    <Box className="xn-virtualCard__top">
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, minWidth: 0 }}>
                        <BrandLogo size={52} />
                        <Typography className="xn-virtualCard__name" sx={{ fontSize: { xs: 16, md: 18 }, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          xNaMai
                        </Typography>
                      </Box>
                      <span className="xn-pill">
                        <span className="xn-mono" style={{ opacity: 0.7 }}>Válido até</span>
                        <span style={{ fontWeight: 900 }}>{validade}</span>
                      </span>
                    </Box>

                    <Box sx={{ mt: 2.2 }}>
                      <Typography sx={{ fontWeight: 950, fontSize: { xs: 20, md: 26 }, letterSpacing: -0.6, color: "#16325c" }}>
                        {headingName}
                      </Typography>
                      <Typography className="xn-muted" sx={{ mt: 0.6, fontWeight: 700 }}>
                        Área da conta • Cupom e números cativos
                      </Typography>
                    </Box>
                  </Box>

                  <Box className="xn-statCard">
                    <Stack spacing={1.3}>
                      <Box>
                        <Typography className="xn-statLabel xn-mono">CÓDIGO DE DESCONTO</Typography>
                        <Typography className="xn-code xn-mono">{couponCode}</Typography>
                      </Box>
                      <Divider sx={{ borderColor: "rgba(219,232,255,0.85)" }} />
                      <Box>
                        <Typography className="xn-statLabel xn-mono">VALOR ACUMULADO</Typography>
                        <Typography className="xn-value">
                          {valorAcumulado.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        <span className="xn-pill">
                          <span style={{ opacity: 0.75 }}>Seus cativos</span>
                          <span style={{ fontWeight: 900 }}>{claims?.mine?.length || 0}</span>
                        </span>
                        <span className="xn-pill">
                          <span style={{ opacity: 0.75 }}>Ocupados</span>
                          <span style={{ fontWeight: 900 }}>{claims?.taken?.length || 0}</span>
                        </span>
                      </Box>
                    </Stack>
                  </Box>
                </Box>
              </Paper>

          {/*
            Seção de Números Cativos temporariamente ocultada.
            Motivo: funcionalidade ainda não será exibida nesta versão.

            ====== Números cativos ======
            <Paper className="xn-card" variant="outlined" sx={{ p: { xs: 2, md: 2.5 } }}>
              <Stack spacing={1.6}>
                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
                  <Box>
                    <Typography variant="h6" className="xn-sectionTitle">Números cativos</Typography>
                    <Typography variant="body2" className="xn-muted" sx={{ mt: 0.6, maxWidth: 780 }}>
                      Garanta seus números preferidos em todo sorteio novo. Configure um cartão e o sistema compra automaticamente quando o sorteio abre.
                    </Typography>
                  </Box>
                  <Button variant="contained" className="xn-btnPrimary" onClick={() => setAutoOpen(true)}>
                    CONFIGURAR NÚMERO CATIVO
                  </Button>
                </Box>

                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
                  <Chip size="small" label="Seu cativo" sx={{ bgcolor: "rgba(37,109,255,0.14)", color: "#16325c", border: "1px solid rgba(37,109,255,0.28)", fontWeight: 900 }} />
                  <Chip size="small" label="Ocupado" sx={{ bgcolor: "rgba(11,27,51,0.06)", color: "rgba(22,50,92,0.70)", border: "1px solid rgba(11,27,51,0.10)", fontWeight: 900 }} />
                  <Chip size="small" label="Livre" sx={{ bgcolor: "#fff", color: "rgba(22,50,92,0.72)", border: "1px solid rgba(37,109,255,0.22)", fontWeight: 900 }} />
                </Stack>

                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
                  <Typography className="xn-muted" sx={{ fontWeight: 700 }}>
                    Selecionados: <b>{claims?.mine?.length || 0}</b> • Para alterar, use “Configurar número cativo”.
                  </Typography>
                </Box>

                {(claims?.mine?.length || 0) + (claims?.taken?.length || 0) === 0 ? (
                  <Box className="xn-emptyState">
                    <Typography sx={{ fontWeight: 900, color: "#16325c" }}>Nenhum dado de números cativos ainda.</Typography>
                    <Typography className="xn-muted" sx={{ mt: 0.4, fontWeight: 700 }}>
                      Clique em “Configurar número cativo” para escolher seus números e ativar a compra automática.
                    </Typography>
                  </Box>
                ) : (
                  <Box className="xn-numbersGrid" sx={{ mt: 0.5 }}>
                    {Array.from({ length: 100 }, (_, n) => {
                      const isMine = claims.mine.includes(n);
                      const isTaken = claims.taken.includes(n);
                      const cls = isMine ? "xn-numberPill xn-numberPill--mine" : isTaken ? "xn-numberPill xn-numberPill--taken" : "xn-numberPill xn-numberPill--free";
                      return (
                        <Box key={n} className={cls}>
                          {String(n).padStart(2, "0")}
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Stack>
            </Paper>
          */}

          <Paper className="xn-card" variant="outlined" sx={{ p: { xs: 2, md: 2.5 } }}>
            <Stack spacing={1.6}>
              <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
                <Box>
                  <Typography variant="h6" className="xn-sectionTitle">
                    Histórico de compras
                  </Typography>
                  <Typography variant="body2" className="xn-muted" sx={{ mt: 0.5, fontWeight: 700 }}>
                    Veja suas compras pagas e o último sorteio em que você participou.
                  </Typography>
                </Box>

                <Chip
                  label={
                    purchaseHistoryLoading
                      ? "Carregando..."
                      : purchaseHistory.length
                        ? `${purchaseHistory.length} compra(s)`
                        : "Sem compras pagas"
                  }
                  sx={{
                    bgcolor: "rgba(37,109,255,0.10)",
                    color: "#16325c",
                    fontWeight: 900,
                    borderRadius: 999,
                    border: "1px solid rgba(37,109,255,0.22)",
                  }}
                />
              </Box>

              {lastPurchase && (
                <Box
                  sx={{
                    p: 1.6,
                    borderRadius: 3,
                    bgcolor: "rgba(37,109,255,0.06)",
                    border: "1px solid rgba(37,109,255,0.16)",
                  }}
                >
                  <Typography className="xn-statLabel xn-mono">
                    ÚLTIMO SORTEIO COMPRADO
                  </Typography>

                  <Typography sx={{ mt: 0.5, fontWeight: 950, color: "#16325c" }}>
                    {lastPurchase.draw_title || "Sorteio"}
                  </Typography>

                  <Typography className="xn-muted" sx={{ mt: 0.4, fontWeight: 700 }}>
                    Número(s): {lastPurchase.numbers_label || "--"} • Valor:{" "}
                    {lastPurchase.amount_label || formatMoneyBRFromCents(lastPurchase.amount_cents)} •{" "}
                    {formatDateTimeBR(lastPurchase.purchased_at || lastPurchase.paid_at)}
                  </Typography>
                </Box>
              )}

              {purchaseHistoryError && (
                <Alert severity="warning" variant="outlined">
                  {purchaseHistoryError}
                </Alert>
              )}

              {purchaseHistoryLoading ? (
                <Box sx={{ px: 0.5, py: 1 }}>
                  <LinearProgress />
                  <Typography className="xn-muted" sx={{ mt: 1, fontWeight: 700 }}>
                    Carregando histórico...
                  </Typography>
                </Box>
              ) : purchaseHistory.length === 0 ? (
                <Box className="xn-emptyState">
                  <Typography sx={{ fontWeight: 900, color: "#16325c" }}>
                    Nenhuma compra paga encontrada.
                  </Typography>
                  <Typography className="xn-muted" sx={{ mt: 0.4, fontWeight: 700 }}>
                    Quando um pagamento for aprovado, ele aparecerá neste histórico.
                  </Typography>
                </Box>
              ) : (
                <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
                  <Table size="small" sx={{ minWidth: { xs: 720, sm: 920 } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 900, whiteSpace: "nowrap", color: "rgba(22,50,92,0.72)" }}>
                          TIPO
                        </TableCell>
                        <TableCell sx={{ fontWeight: 900, whiteSpace: "nowrap", color: "rgba(22,50,92,0.72)" }}>
                          SORTEIO
                        </TableCell>
                        <TableCell sx={{ fontWeight: 900, whiteSpace: "nowrap", color: "rgba(22,50,92,0.72)" }}>
                          NÚMEROS
                        </TableCell>
                        <TableCell sx={{ fontWeight: 900, whiteSpace: "nowrap", color: "rgba(22,50,92,0.72)" }}>
                          VALOR
                        </TableCell>
                        <TableCell sx={{ fontWeight: 900, whiteSpace: "nowrap", color: "rgba(22,50,92,0.72)" }}>
                          DATA
                        </TableCell>
                        <TableCell sx={{ fontWeight: 900, whiteSpace: "nowrap", color: "rgba(22,50,92,0.72)" }}>
                          STATUS
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {purchaseHistory.map((item, index) => (
                        <TableRow key={`${item.type || "purchase"}-${item.id || index}`} hover>
                          <TableCell sx={{ fontWeight: 800 }}>
                            {item.type_label || (item.type === "promotional" ? "Promocional" : "Principal")}
                          </TableCell>

                          <TableCell sx={{ fontWeight: 800 }}>
                            {formatDrawLabel(item)}
                          </TableCell>

                          <TableCell sx={{ fontWeight: 800 }}>
                            {item.numbers_label || "--"}
                          </TableCell>

                          <TableCell sx={{ fontWeight: 800 }}>
                            {item.amount_label || formatMoneyBRFromCents(item.amount_cents)}
                          </TableCell>

                          <TableCell>
                            {formatDateTimeBR(item.purchased_at || item.paid_at)}
                          </TableCell>

                          <TableCell>
                            <Chip
                              label="PAGO"
                              sx={{
                                bgcolor: "rgba(37,109,255,0.12)",
                                color: "#16325c",
                                fontWeight: 900,
                                borderRadius: 999,
                                px: 1.5,
                                border: "1px solid rgba(37,109,255,0.26)",
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Stack>
          </Paper>

          {/* Tabela */}
              <Paper className="xn-card" variant="outlined" sx={{ p: { xs: 2, md: 2.5 } }}>
                <Stack spacing={1.4}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
                    <Typography variant="h6" className="xn-sectionTitle">Minhas participações</Typography>
                    <Typography className="xn-muted" sx={{ fontWeight: 700 }}>
                      {rows.length ? `${rows.length} registro(s)` : "—"}
                    </Typography>
                  </Box>

                  {loadingReservations ? (
                    <Box sx={{ px: 0.5, py: 1 }}>
                      <LinearProgress />
                      <Typography className="xn-muted" sx={{ mt: 1, fontWeight: 700 }}>
                        Carregando participações...
                      </Typography>
                    </Box>
                  ) : (
                    <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
                      {promotionalError && (
                        <Alert severity="warning" variant="outlined" sx={{ mb: 1.5 }}>
                          {promotionalError}
                        </Alert>
                      )}
                      <Table size="small" sx={{ minWidth: { xs: 720, sm: 920 } }}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 900, whiteSpace: "nowrap", color: "rgba(22,50,92,0.72)" }}>SORTEIO</TableCell>
                            <TableCell sx={{ fontWeight: 900, whiteSpace: "nowrap", color: "rgba(22,50,92,0.72)" }}>NÚMERO</TableCell>
                            <TableCell sx={{ fontWeight: 900, whiteSpace: "nowrap", color: "rgba(22,50,92,0.72)" }}>DIA</TableCell>
                            <TableCell sx={{ fontWeight: 900, whiteSpace: "nowrap", color: "rgba(22,50,92,0.72)" }}>PAGAMENTO</TableCell>
                            <TableCell sx={{ fontWeight: 900, whiteSpace: "nowrap", color: "rgba(22,50,92,0.72)" }}>STATUS</TableCell>
                            <TableCell sx={{ fontWeight: 900, whiteSpace: "nowrap", color: "rgba(22,50,92,0.72)" }} align="right">PAGAR</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {!loadingReservations && rows.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={6}>
                                <Box className="xn-emptyState">
                                  <Typography sx={{ fontWeight: 900, color: "#16325c" }}>Nenhuma participação encontrada.</Typography>
                                  <Typography className="xn-muted" sx={{ mt: 0.4, fontWeight: 700 }}>
                                    Quando você participar de um sorteio, ele aparecerá aqui.
                                  </Typography>
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                          {rows.map((row, idx) => {
                      const rowLoadingPixId =
                        row?.reservationId ||
                        row?.reservation_id ||
                        row?.id ||
                        `${row?.type || "main"}-${row?.drawId || row?.draw_id || row?.sorteio}`;
                      const paymentStatus = String(
                        row.paymentStatus ?? row.payment_status ?? row.pagamento ?? ""
                      ).toLowerCase();
                      const reservationStatus = String(
                        row.status ?? row.resultado ?? "reserved"
                      ).toLowerCase();
                      const isPromotionalRow =
                        row.type === "promotional" ||
                        row.source === "promotional" ||
                        row.is_promotional === true;
                      const isPaid =
                        isPaidStatus(paymentStatus) || isPaidStatus(reservationStatus);
                      const isExpired = isReservationExpired(row);
                      const canGeneratePix =
                        !isPaid &&
                        !isExpired &&
                        (row.can_pay === true || row.canPay === true);
                      const clickable = !isPromotionalRow && canGeneratePix;
                      const isClosed = /(closed|fechado|sorteado)/i.test(String(row.resultado || ""));
                      const isOpen   = /(open|aberto)/i.test(String(row.resultado || ""));

                      const handleRowClick = () => {
                        if (!clickable) return;
                        const drawId = Number(row.draw_id ?? row.sorteio);
                        if (isPaid && isClosed && Number.isFinite(drawId)) navigate(`/me/draw/${drawId}`);
                        else if (isOpen) navigate("/");
                      };

                      return (
                        <TableRow
                          key={`${row.type || "main"}-${row.sorteio}-${row.id || idx}`}
                          hover
                          onClick={clickable ? handleRowClick : undefined}
                          sx={{ cursor: clickable ? "pointer" : "default" }}
                        >
                          <TableCell sx={{ width: 100, fontWeight: 700 }}>
                            {formatDrawLabel(row)}
                          </TableCell>
                          <TableCell sx={{ minWidth: 160, fontWeight: 700 }}>
                            {isPromotionalRow
                              ? (row.numbers || "--")
                              : row.numbers_label || (Array.isArray(row.numeros) ? row.numeros.map(pad2).join(", ") : (row.numero != null ? pad2(row.numero) : "--"))}
                          </TableCell>
                          <TableCell sx={{ width: 140 }}>{row.dia}</TableCell>
                          <TableCell>
                            <PayChip
                              status={
                                isPaid
                                  ? "paid"
                                  : isExpired
                                    ? "expired"
                                    : row.pagamento
                              }
                            />
                          </TableCell>
                          <TableCell>
                            {isPromotionalRow ? (
                              <PlainStatusChip status={row.resultado} />
                            ) : (
                              <ResultChip result={row.resultado} />
                            )}
                          </TableCell>
                          <TableCell align="right" sx={{ width: 120 }}>
                            {canGeneratePix && !isPaid ? (
                              <Button
                                size="small"
                                variant="contained"
                                className="xn-btnPrimary btn-generate-pix"
                                disabled={loadingPixId === rowLoadingPixId}
                                onClick={(e) => { e.stopPropagation(); handleGeneratePix(row); }}
                              >
                                {loadingPixId === rowLoadingPixId ? "GERANDO..." : "GERAR PIX"}
                              </Button>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}

                  <Box className="xn-actionsRow" sx={{ pt: 1 }}>
                    <Button
                      variant="outlined"
                      className="xn-btnSoft"
                      disabled={loadingCoupon}
                      onClick={async () => {
                        await syncAndReloadCoupon();
                        window.open(XNAMAI_SITE_URL, "_blank", "noopener,noreferrer");
                      }}
                      sx={{
                        borderColor: "rgba(37,109,255,0.26)",
                        color: "#16325c",
                        bgcolor: "rgba(255,255,255,0.65)",
                        "&:hover": { borderColor: "rgba(37,109,255,0.46)", bgcolor: "rgba(37,109,255,0.06)" },
                      }}
                    >
                      {loadingCoupon ? "ATUALIZANDO..." : "RESGATAR CUPOM"}
                    </Button>
                    <Button
                      variant="text"
                      onClick={doLogout}
                      className="xn-btnSoft"
                      sx={{ color: "rgba(22,50,92,0.72)", "&:hover": { bgcolor: "rgba(11,27,51,0.04)" } }}
                    >
                      SAIR
                    </Button>
                  </Box>
                </Stack>
              </Paper>
            </Stack>
          </Container>
        </Box>
      </Box>

      {/* Modal de PIX */}
      <PixModal
        open={pixOpen}
        onClose={() => setPixOpen(false)}
        loading={pixLoading}
        data={pixData}
        amount={pixAmount}
        inlineMessage={pixMsg}
        onCopy={copyPix}
        onRefresh={refreshPix}
      />

      {/* Modal: configuração de compra automática */}
      <Dialog
        open={autoOpen}
        onClose={handleCloseAutoModal}
        fullWidth
        maxWidth="lg"
        PaperProps={{ className: "xn-accountModalMuiPaper" }}
        BackdropProps={{ sx: { backgroundColor: "rgba(9, 18, 35, 0.38)" } }}
        sx={{
          "& .MuiDialog-container": { alignItems: { xs: "flex-end", md: "center" } },
        }}
      >
        <DialogContent className="xn-accountModalContent" dividers={false}>
          <Box className="xn-accountModalClose">
            <IconButton onClick={handleCloseAutoModal} aria-label="Fechar" sx={{ bgcolor: "rgba(255,255,255,0.72)", border: "1px solid rgba(219,232,255,0.95)" }}>
              <CloseRoundedIcon />
            </IconButton>
          </Box>
          <Box className="xn-autopayWrap">
            <AutoPaySection />
          </Box>
        </DialogContent>
      </Dialog>
    </ThemeProvider>
  );
}
